// src/components/MatrixRainBackground.tsx

import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

const StyledCanvas = styled.canvas`
  position: fixed;
  top: 0;
  left: 0;
  z-index: -1;
  width: 100vw;
  max-width: 100%;
  max-height: 100%;
  background-color: #000;
  opacity: 0.7;
  overflow: hidden;
`;

const MatrixRainBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const intervalIdRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;
    
    // --- PARAMETERS TUNED FOR A CLEAN, FADING EFFECT ---
    const FONT_SIZE = 18;
    // A lower alpha value creates longer, smoother trails
    const FADE_EFFECT = 'rgba(0, 0, 0, 0.09)'; 
    const CHARACTERS = '01';//'アァカサタナハマヤラワガザダバパイキシチニヒミリヰギジヂビピウゥクスツヌフムユルグズブプエェケセテネヘメレヱゲゼデベペオォコソトノホモヨロヲゴゾドボポヴッン0123456789';
    // Increased frame rate for smoother animation
    const FRAME_RATE = 1000 / 20; 
    const MAX_ACTIVE_DROPS = 20;

    type Drop = {
      x: number; // The column (horizontal position)
      y: number; // The row (vertical position)
    };

    // These are now managed by the new logic
    let columns: number;
    let drops: Drop[]; // This is now our "object pool"

    const initializeAndResize = () => {
      const dpr = window.devicePixelRatio || 1;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      canvas.width = viewportWidth * dpr;
      canvas.height = viewportHeight * dpr;
      canvas.style.width = `${viewportWidth}px`;
      canvas.style.height = `${viewportHeight}px`;

      context.scale(dpr, dpr);

      columns = Math.floor(viewportWidth / FONT_SIZE);
      drops = [];

      // ===================================================================
      // 2. NEW: Populate the object pool with a limited number of drops
      // ===================================================================
      const usedColumns = new Set<number>(); // To ensure no two drops start in the same column

      for (let i = 0; i < MAX_ACTIVE_DROPS; i++) {
        // Stop if we've used all available columns
        if (usedColumns.size >= columns) break;

        let randomColumn: number;
        // Find a column that isn't already in use
        do {
          randomColumn = Math.floor(Math.random() * columns);
        } while (usedColumns.has(randomColumn));

        usedColumns.add(randomColumn);

        // Add a new drop object to our pool
        drops.push({
          x: randomColumn,
          // Random negative start acts as a delay, so they appear staggered
          y: -Math.floor(Math.random() * (viewportHeight / FONT_SIZE)),
        });
      }
    };

    // Define these at the top of your script
    const LEAD_COLOR = 'limegreen'; // Light green/white core
    const TRAIL_LENGTH = 30; // Controls how long the trail is

    const draw = () => {
      // Fade the canvas. IMPORTANT: Ensure no shadow is active here.
      context.shadowBlur = 0; // Reset just in case
      context.fillStyle = FADE_EFFECT;
      context.fillRect(0, 0, window.innerWidth, window.innerHeight);

      // Set up the glow and text properties
      context.font = `${FONT_SIZE}px "JetBrains Mono", monospace`;

      // Loop over the pool of drops
      for (const drop of drops) {
        // Recycle the drop if it's off-screen
        if ((drop.y - TRAIL_LENGTH) * FONT_SIZE > window.innerHeight) {
          // Get all currently used columns
          const usedColumns = new Set(drops.map(d => d.x).filter(x => x !== drop.x));

          // Pick a random new column
          let newX = Math.floor(Math.random() * columns);

          // If our random pick is already in use, just nudge it to the next column
          if (usedColumns.has(newX)) {
              newX = (newX + 1) % columns; // The % columns makes it wrap around
          }

          // Recycle the drop
          drop.x = newX;
          drop.y = 0;
        }

        const text = CHARACTERS.charAt(Math.floor(Math.random() * CHARACTERS.length));

        context.fillStyle = LEAD_COLOR;
        // Draw the glowing character
        context.fillText(text, drop.x * FONT_SIZE, drop.y * FONT_SIZE);

        // --- Draw the Solid Black Eraser Trail ---
        // This is the correct way to erase the end of the trail
        const eraseY = (drop.y - TRAIL_LENGTH) * FONT_SIZE;

        if (eraseY > 0) { // Only draw the eraser if it's on the screen
          context.fillStyle = '#000'; // Set to solid black
          // Use fillRect to draw a solid block, not fillText
          context.fillRect(drop.x * FONT_SIZE, eraseY, FONT_SIZE, FONT_SIZE);
        }
        // Move the drop down
        drop.y++;
      }
    };

    initializeAndResize();
    intervalIdRef.current = setInterval(draw, FRAME_RATE);
    window.addEventListener('resize', initializeAndResize);

    return () => {
      if (intervalIdRef.current) clearInterval(intervalIdRef.current);
      window.removeEventListener('resize', initializeAndResize);
    };
  }, []);

  return <StyledCanvas ref={canvasRef} />;
};

export default MatrixRainBackground;